import {
	dispatchAcknowledgedEvent,
	openResourceSection,
} from '../../core/resourceEvents';
import {highlightExistingAction} from '../../core/actionHighlighter';
import {
	SITE_BACKUP_FILTER_EVENT,
	SITE_BACKUP_FOCUS_EVENT,
	SITE_BACKUP_SECTION_EVENT,
} from './events';

const SITE_BACKUP_SECTIONS = new Set(['maintenance.site_backup']);

/**
 * Open the Site Backup accordion and carry out a highlighted toolbar action.
 *
 * `refresh` and `bulk_select_delete` never resolve an entity - the backend
 * workflow templates for both are section-only (`opens_section`, no
 * resolver) - so both are worked here exactly as alarms works them, by
 * finding and highlighting (or clicking, when `action_activation` is
 * `auto`) the existing Refresh / Delete control on the table.
 *
 * @param {object} command experience command
 * @return {Promise<void>} completion
 */
export async function openSiteBackupSection(command = {}) {
	const seen = await openResourceSection(
		SITE_BACKUP_SECTION_EVENT,
		'maintenance.site_backup',
		SITE_BACKUP_SECTIONS,
	);
	const target = command.params?.action_target;
	const activation = {
		activate: command.params?.action_activation === 'auto',
		commandId: command.command_id,
	};
	if (target === 'refresh' &&
		!highlightExistingAction(['Refresh'], [], activation)) {
		throw new Error('site_backup_refresh_unavailable');
	}
	if (target === 'bulk_select_delete' &&
		!highlightExistingAction(['Delete'], [], activation)) {
		throw new Error('site_backup_bulk_selection_unavailable');
	}
	// Carried back rather than awaited and dropped. The section says
	// what the page actually opened; a wrapper returning nothing
	// turns that into an empty acknowledgement, and the backend then
	// cannot tell a section that opened from one that did not.
	return seen;
}

/**
 * Apply a Site Backup list query or column filter.
 * @param {object} command filter command
 * @return {Promise<void>} completion acknowledgement
 */
export function applySiteBackupFilter(command) {
	const params = command.params || {};
	const query = String(params.query || '').trim();
	const filters = {};
	Object.entries(params).forEach(([key, value]) => {
		if (key === 'query' || key === 'resource') return;
		const text = String(value ?? '').trim();
		if (text) filters[key] = text;
	});
	if (!query && !Object.keys(filters).length) {
		return Promise.reject(new Error('invalid_site_backup_filter'));
	}
	return dispatchAcknowledgedEvent(SITE_BACKUP_FILTER_EVENT, {
		query,
		...filters,
		resource: 'site_backup',
	});
}

/**
 * Focus a resolved Site Backup row, and download it when asked.
 *
 * The `download` workflow template resolves an entity and opens the
 * section same as `search`, then carries `highlight_action: "download"` -
 * so a resolved download, like a resolved report download, is worked here
 * rather than through a modal: there is nothing to confirm.
 *
 * @param {object} command focus command
 * @return {Promise<void>} completion acknowledgement
 */
export function focusSiteBackup(command) {
	const entityId = command.params?.entity_id;
	if (typeof entityId !== 'string' || !entityId) {
		return Promise.reject(new Error('invalid_site_backup_entity'));
	}
	return dispatchAcknowledgedEvent(SITE_BACKUP_FOCUS_EVENT, {
		entityId,
		action_target: command.params?.action_target,
		action_activation: command.params?.action_activation,
		resource: 'site_backup',
	});
}
